import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthContext';
import Input from '@shared/Input';
import Button from '@shared/Button'; // Corrected import statement
import { useNavigate } from 'react-router-dom';
import { MessageSquare } from "lucide-react";
import T from "@/utils/i18n";
import ForgotPasswordModal from '@/components/auth/ForgotPasswordModal'; // Import the new modal

const LoginForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false); // State for modal visibility

  const { login, loading, error } = useAuth();
  const navigate = useNavigate();

  const validateForm = (): boolean => {
    let isValid = true;
    setEmailError('');
    setPasswordError('');

    if (!email) {
      setEmailError('Email is required.');
      isValid = false;
    } else if (!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(email)) { // Updated regex for email validation
      setEmailError('Invalid email format.');
      isValid = false;
    }

    if (!password) {
      setPasswordError('Password is required.');
      isValid = false;
    } else if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const success = await login(email, password);
      if (success) {
        navigate('/dashboard'); // Redirect to dashboard on successful login
      }
    }
  };

  return (
    <div className="flex flex-col md:flex-row items-center justify-center min-h-screen bg-gray-100 p-4">
      <div className="w-full max-w-md p-8 space-y-6 ">
        <Link to="/" className={`flex items-center gap-3 mb-10 justify-center`}>
          <h1 className="text-3xl font-bold text-center text-gray-900">
            <div className={`flex items-center  space-x-2`}>
              <MessageSquare className={` w-9 md:w-12 h-9 md:h-12 text-blue-600`} />
              <span className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                    {T.product.name["en"]} {/* Product name*/}
                  </span>
            </div>
          </h1>
        </Link>
      </div>
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8 space-y-6">
        <h2 className="text-3xl font-bold text-center text-gray-900">Sign In</h2>
        <p className="text-center text-gray-600">Welcome back! Please enter your details.</p>

        {error && (
          <div className="bg-error/10 text-error p-3 rounded-md text-sm text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <Input
              id="email"
              type="email"
              placeholder="your@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!emailError}
              className="w-full"
            />
            {emailError && <p className="text-error text-xs mt-1">{emailError}</p>}
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <Input
              id="password"
              type="password"
              placeholder="********"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!passwordError}
              className="w-full"
            />
            {passwordError && <p className="text-error text-xs mt-1">{passwordError}</p>}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                Remember me
              </label>
            </div>
            <button
              type="button"
              onClick={() => setShowForgotPasswordModal(true)}
              className="text-sm font-medium text-primary-600 hover:text-primary-500"
            >
              Forgot your password?
            </button>
          </div>

          <Button
            type="submit"
            className="w-full"
            loading={loading}
            disabled={loading}
            variant="primary"
            size="lg"
          >
            Sign In
          </Button>
        </form>
      </div>
      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
      />
    </div>
  );
};

export default LoginForm;
